import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { ChevronDown } from 'react-feather';
import { Dropdown } from '@/shared/components/Dropdown';
import { Tooltip } from '@/shared/components/Tooltip';
import type { MenuItemProps } from '@/shared/components/Menu/types';
import { listTaskForms } from '@/epics/taskFormManagement/api/listTaskFormsApiService';
import { ASSIGN_TECHNICIAN_LABELS as L } from '../../constants/labels';
import { useDebounce } from '@/shared/hooks/useDebounce';
import type {
  TaskFormListItem,
  TaskFormListItemCertificate,
} from '@/epics/taskFormManagement/types';

const PAGE_LIMIT = 10;

export interface TaskFormDropdownProps {
  selectedId: string;
  onSelect: (
    id: string,
    formId: string,
    name: string,
    certificate: TaskFormListItemCertificate | null
  ) => void;
  label: ReactNode;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
}

const TaskFormDropdownComponent = function TaskFormDropdown({
  selectedId,
  onSelect,
  label,
  disabled = false,
  error = false,
  errorMessage,
}: TaskFormDropdownProps) {
  const [forms, setForms] = useState<TaskFormListItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);

  // Synchronous lock — React state has a render-cycle delay so using it as a
  // guard in fetchMore lets rapid onScrollEnd events bypass the check and fire
  // concurrent fetches before the loading state re-render lands.
  const loadingRef = useRef(false);

  // Incremented every time debouncedSearch changes. fetchMore captures the
  // value at call time and discards its result if the counter advanced while
  // the API call was in-flight — prevents stale pages from being appended on
  // top of a reset list (e.g. when the user clears the search mid-scroll).
  const fetchGenRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    fetchGenRef.current += 1;
    const gen = fetchGenRef.current;
    setForms([]);
    setPage(1);
    setHasMore(true);
    setFetchError('');
    const fetchFirstPage = async () => {
      loadingRef.current = true;
      setLoading(true);
      const result = await listTaskForms({
        page: 1,
        limit: PAGE_LIMIT,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      if (!mounted || fetchGenRef.current !== gen) {
        loadingRef.current = false;
        return;
      }
      if (result.success) {
        setForms(result.data?.data ?? []);
        setHasMore(result.data?.pagination.hasNextPage ?? false);
        setPage(1);
      } else {
        setFetchError(result.error ?? '');
      }
      setLoading(false);
      loadingRef.current = false;
    };
    void fetchFirstPage();
    return () => {
      mounted = false;
    };
  }, [debouncedSearch]);

  const fetchMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) {
      return;
    }
    loadingRef.current = true;
    setLoading(true);
    const gen = fetchGenRef.current;
    const nextPage = page + 1;
    const result = await listTaskForms({
      page: nextPage,
      limit: PAGE_LIMIT,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    });
    // debouncedSearch changed while this fetch was in-flight — discard to
    // avoid appending results from the wrong search session.
    if (fetchGenRef.current !== gen) {
      loadingRef.current = false;
      return;
    }
    if (result.success) {
      setForms((prev) => {
        const incoming = result.data?.data ?? [];
        const ids = new Set(prev.map((f) => f.id));
        return [...prev, ...incoming.filter((f) => !ids.has(f.id))];
      });
      setHasMore(result.data?.pagination.hasNextPage ?? false);
      setPage(nextPage);
    } else {
      setHasMore(false);
      setFetchError(result.error ?? '');
    }
    setLoading(false);
    loadingRef.current = false;
  }, [hasMore, page, debouncedSearch]);

  const items: MenuItemProps[] = forms.map((f) => ({
    id: f.id,
    label: `${f.name} (${f.form_id})`,
    toolTip: false as const,
  }));

  if (loading) {
    items.push({
      id: '__loading__',
      label: L.TASK_FORM_LOADING,
      disabled: true,
    });
  } else if (fetchError && forms.length === 0) {
    items.push({ id: '__error__', label: fetchError, disabled: true });
  } else if (!loading && forms.length === 0) {
    items.push({
      id: '__empty__',
      label: L.TASK_FORM_NO_RESULTS,
      disabled: true,
    });
  }

  const selected = forms.find((f) => f.id === selectedId) ?? null;

  return (
    <Dropdown
      menuFullWidth
      toolTip={false}
      showSearch
      searchPlaceholder="Search"
      menuMaxVisibleItems={5}
      autoMaxHeight
      items={items}
      {...(selectedId ? { selectedId } : {})}
      disabled={disabled}
      error={error}
      {...(errorMessage ? { errorMessage } : {})}
      onSearchChange={setSearchQuery}
      onItemClick={(id) => {
        if (id !== '__loading__' && id !== '__empty__' && id !== '__error__') {
          const form = forms.find((f) => f.id === id);
          onSelect(
            id,
            form?.form_id ?? '',
            form?.name ?? '',
            form?.certificate ?? null
          );
        }
      }}
      onScrollEnd={() => {
        if (hasMore && !loadingRef.current) {
          void fetchMore();
        }
      }}
      trigger={({
        open,
        toggle,
        disabled: triggerDisabled,
        error: triggerError,
      }) => {
        const labelFloating = open || !!selected;
        const labelColor = open ? '#2a4455' : '#6a7278';
        return (
          <button
            type="button"
            onClick={triggerDisabled ? undefined : toggle}
            aria-expanded={open}
            aria-haspopup="true"
            aria-disabled={triggerDisabled}
            data-testid="assign-task-form-trigger"
            className={`w-full flex items-center justify-between gap-2 bg-transparent outline-none text-left ${
              triggerDisabled
                ? 'cursor-not-allowed text-neutral-60'
                : 'cursor-pointer text-neutral-10'
            }`}
          >
            <div
              className="flex-1 min-w-0 relative flex flex-col justify-center"
              style={{ minHeight: 40 }}
            >
              <span
                className="absolute left-0 pointer-events-none origin-top-left font-normal"
                style={{
                  color: labelColor,
                  top: labelFloating ? 0 : 10,
                  fontSize: labelFloating ? 12 : 16,
                  lineHeight: labelFloating ? '16px' : '20px',
                  transition:
                    'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                }}
              >
                {label}
              </span>
              {selected && (
                <Tooltip
                  content={selected.name}
                  side="bottom"
                  align="left"
                  triggerTabIndex={-1}
                >
                  <span
                    className="text-neutral-10 block w-full truncate"
                    style={{
                      marginTop: 16,
                      fontSize: 14,
                      lineHeight: '20px',
                      fontWeight: 400,
                    }}
                  >
                    {selected.name}
                  </span>
                </Tooltip>
              )}
            </div>
            <ChevronDown
              strokeWidth={1.5}
              className={`shrink-0 transition-transform duration-200 ${
                open ? 'rotate-180' : ''
              } ${triggerError ? 'text-error-40' : ''}`}
            />
          </button>
        );
      }}
    />
  );
};

export const TaskFormDropdown = React.memo(TaskFormDropdownComponent);
